import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { fetchUsageSummary } from "@/lib/api";
import type { LlmUsageSummaryResponse } from "@school-sales-ai/shared";

export type DatePreset = "7d" | "30d" | "90d" | "custom";

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function presetDates(preset: Exclude<DatePreset, "custom">): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  const days = preset === "7d" ? 7 : preset === "90d" ? 90 : 30;
  from.setDate(from.getDate() - days);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

export type UseUsageMetricsReturn = {
  data: LlmUsageSummaryResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  preset: DatePreset;
  setPreset: (p: DatePreset) => void;
  customFrom: string;
  setCustomFrom: (v: string) => void;
  customTo: string;
  setCustomTo: (v: string) => void;
  channel: string;
  setChannel: (v: string) => void;
  from: string;
  to: string;
};

export function useUsageMetrics(): UseUsageMetricsReturn {
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);
  const [preset, setPreset] = useState<DatePreset>("30d");
  const [customFrom, setCustomFrom] = useState<string>(() => toIsoDate(new Date(Date.now() - 30 * 86_400_000)));
  const [customTo, setCustomTo] = useState<string>(() => toIsoDate(new Date()));
  const [channel, setChannel] = useState<string>("");

  const { from, to } = useMemo(() => {
    if (preset === "custom") return { from: customFrom, to: customTo };
    return presetDates(preset);
  }, [preset, customFrom, customTo]);

  const { data, isLoading, isError } = useQuery<LlmUsageSummaryResponse>({
    queryKey: ["usage-summary", activeClientId, from, to, channel],
    queryFn: () =>
      fetchUsageSummary({
        clientId: activeClientId!,
        from,
        to,
        channel: channel || undefined,
      }),
    enabled: !!activeClientId,
    staleTime: 60_000,
  });

  return {
    data,
    isLoading,
    isError,
    preset,
    setPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    channel,
    setChannel,
    from,
    to,
  };
}
